import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { noop } from "lodash";
import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import { ApiResources } from "@/api/resources";
import useTableItemDetails from "@/components/DetailsSection/hooks/useTableItemDetails";
import Dialog from "@/components/Dialog/Dialog";
import { DialogWidth } from "@/components/Dialog/types/enums";
import { useAuth } from "@/context/Auth/AuthProvider";
import {
  ANALYSIS_PROVIDER_NAMES,
  VDO_INTEGRATION_FIELD_NAMES,
  VdoErrors,
  VdoObjectState
} from "@/Pages/Integrations/types";
import { useMediaQueries } from "@/shared";
import DialogActions from "@/shared/components/buttons/DialogActions";
import useResponse from "@/shared/hooks/useResponse";
import { useIntegrationsDialogStore } from "@/stores/useIntegrationsDialogStore";
import { useAnalysisProvidersTableStore } from "@/stores/useTableStore";

import VdoIntegrationContent from "../VdoIntegrationContent";

interface AddVdoIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddVdoIntegration = ({ isOpen, onClose }: AddVdoIntegrationProps) => {
  const { t } = useTranslation();
  const { toMd } = useMediaQueries();
  const { getTableItemDetails } = useTableItemDetails();
  const { postData } = useAxiosPrivate();
  const { user } = useAuth();
  const { handleCommonError } = useResponse();

  const analysisProvidersTableParams = useAnalysisProvidersTableStore(
    state => state.params
  );

  const goBackToTypeSelect = useIntegrationsDialogStore(
    state => state.goBackToTypeSelect
  );

  const initialVDOObject: VdoObjectState = {
    [VDO_INTEGRATION_FIELD_NAMES.ApiKey]: "",
    [VDO_INTEGRATION_FIELD_NAMES.AccountId]: "",
    [VDO_INTEGRATION_FIELD_NAMES.SelectedCompany]:
      user?.selectedCompany ?? null,
    [VDO_INTEGRATION_FIELD_NAMES.Name]: "",
    [VDO_INTEGRATION_FIELD_NAMES.Enabled]: true,
    [VDO_INTEGRATION_FIELD_NAMES.IncludeSubCompanies]: false,
    [VDO_INTEGRATION_FIELD_NAMES.FileFormat]: null
  };

  const initialErrors: VdoErrors = {
    [VDO_INTEGRATION_FIELD_NAMES.ApiKey]: "",
    [VDO_INTEGRATION_FIELD_NAMES.AccountId]: "",
    [VDO_INTEGRATION_FIELD_NAMES.Name]: ""
  };

  const [vdoObject, setVdoObject] = useState<VdoObjectState>(initialVDOObject);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<VdoErrors>(initialErrors);

  const resetAllValues = () => {
    setVdoObject(initialVDOObject);
    setErrors(initialErrors);
  };

  useEffect(() => {
    resetAllValues();
  }, [user?.selectedCompany]);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const { selectedCompany, ...restOfThePayload } = vdoObject;

      const payloadObject = {
        ...restOfThePayload,
        provider: ANALYSIS_PROVIDER_NAMES.VDO,
        companyId: vdoObject.selectedCompany?.id,
        fileFormat: vdoObject.fileFormat?.value
      };

      const { data } = await postData(
        ApiResources.IntegrationsAnalysisProviders,
        payloadObject
      );

      const searchParams = analysisProvidersTableParams.contains;

      getTableItemDetails({
        itemId: data.id,
        successMessage: t("Alert##add integration success"),
        leaveTableDetailsNotUpdated: true,
        valueInSearchField: searchParams ?? undefined
      });

      onClose();
      resetAllValues();
    } catch (error) {
      handleCommonError(error, t);
    }
    setIsLoading(false);
  };

  const requiredFieldsFilled = Object.entries(vdoObject).every(
    ([_, value]) =>
      value !== null && value !== undefined && String(value).trim() !== ""
  );

  const hasErrors = Object.values(errors).some(error => error !== "");

  const isReadyToConfirm = !hasErrors && requiredFieldsFilled;

  return (
    <Dialog
      data-testid="add-vdo-integration-dialog"
      title={t("Dialog##vdo integration##add##title")}
      description={t("Dialog##vdo integration##add##description")}
      extendToMaxWidth={toMd}
      isTitleSeparator
      isActionsSeparator
      open={isOpen}
      close={isLoading ? noop : onClose}
      submit={handleSubmit}
      width={toMd ? undefined : DialogWidth.Dialog920px}
      slotProps={{ transition: { onExited: resetAllValues } }}
      actions={
        <DialogActions
          onClose={goBackToTypeSelect}
          disabledClose={isLoading}
          isLoading={isLoading}
          isreadyToConfirm={isReadyToConfirm}
          confirmationButtonText={t("Button##add")}
          confirmationButtonTestId="add-vdo-integration-button"
          cancelButtonText={t("Button##back")}
          cancelButtonTestId="back-vdo-integration-button"
        />
      }
    >
      <VdoIntegrationContent
        isLoading={isLoading}
        vdoObject={vdoObject}
        errors={errors}
        setErrors={setErrors}
        setVdoObject={setVdoObject}
      />
    </Dialog>
  );
};

export default AddVdoIntegration;
